/**
 * `StoreHeader` — the storefront's header chrome, shared by `apps/store` and the
 * ERP composer preview (module 22 editor parity), the same way `HomeSections`
 * is shared. Presentational + class-free (inline CSS custom properties): logo
 * or wordmark, nav (curated links or every collection), a search box, and slots
 * for the interactive cart + account controls.
 *
 * The interactive bits (cart badge count, account menu) differ per surface, so
 * they arrive as `cartBadge` / `accountSlot` render props — the storefront
 * passes its live controls, the preview passes static placeholders. That keeps
 * one visual source of truth and no look-alike drift.
 */
import type { ReactNode } from "react";
import type { StoreHeaderConfig } from "@flint/api-contract/store-home";
import { resolveTarget, isExternal } from "./resolve-target.js";
import { Input } from "../primitives/input.js";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "../primitives/sheet.js";

interface Collection {
  slug: string;
  name: string;
}

const DEFAULTS: StoreHeaderConfig = {
  nav: [],
  autoCollectionNav: true,
  showSearch: true,
  showCart: true,
  showAccount: true,
  sticky: true,
};

export function StoreHeader({
  storeName,
  storeSlug,
  logoUrl,
  logoHeight,
  logoMark,
  config,
  collections,
  phone,
  cartBadge,
  accountSlot,
  searchSlot,
  mobileSearchSlot,
}: {
  storeName: string;
  storeSlug?: string | undefined;
  logoUrl?: string | undefined;
  /** Logo image height in px (from the theme); width scales automatically. */
  logoHeight?: number | undefined;
  /** Brand mark shown beside the wordmark when there's no logo image. */
  logoMark?: ReactNode;
  config?: StoreHeaderConfig | undefined;
  collections?: Collection[] | undefined;
  /** Seller phone — lets a WhatsApp nav target resolve. */
  phone?: string | null | undefined;
  /** Live cart count badge (storefront) — omitted in preview. */
  cartBadge?: ReactNode;
  /** Account / sign-in control (storefront) — a static button in preview. */
  accountSlot?: ReactNode;
  /**
   * Interactive search control (storefront) — the debounced type-ahead with its
   * suggestions dropdown. Omitted in the ERP composer preview, which has no
   * store to query and must render without a browser; the plain GET form below
   * is the fallback, and remains the no-JS path on the storefront too.
   */
  searchSlot?: ReactNode;
  /** Same control for the mobile nav sheet, where the results render inline. */
  mobileSearchSlot?: ReactNode;
}) {
  const c = config ?? DEFAULTS;
  const displayName = storeName.length > 24 ? storeName.slice(0, 24) + "…" : storeName;

  // Custom nav resolves each link's target to an href (dropping any that no
  // longer resolve); with none set, list every collection automatically.
  const ctx = {
    storeSlug: storeSlug ?? "",
    phone: phone ?? null,
    liveCollectionSlugs: new Set((collections ?? []).map((col) => col.slug)),
  };
  const navItems: Array<{ name: string; href: string; external: boolean }> =
    c.nav && c.nav.length > 0
      ? c.nav
          .map((n) => {
            const href = resolveTarget(n.target, ctx);
            return href ? { name: n.label, href, external: isExternal(n.target) } : null;
          })
          .filter((n): n is { name: string; href: string; external: boolean } => n !== null)
      : // No custom links: auto-list collections unless the seller turned that off.
        (c.autoCollectionNav ?? true)
        ? (collections ?? [])
            .slice(0, 5)
            .map((col) => ({ name: col.name, href: `/s/${storeSlug}/c/${col.slug}`, external: false }))
        : [];

  return (
    <header
      style={{
        borderBottom: "1px solid var(--line)",
        backgroundColor: "var(--paper)",
        position: c.sticky ? "sticky" : "static",
        top: 0,
        zIndex: 10,
      }}
    >
      <div style={{ maxWidth: "var(--s-container, 1120px)", margin: "0 auto", padding: "0 clamp(1rem, 4vw, 2.5rem)" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "3.5rem",
            gap: "0.75rem",
          }}
        >
          {/* Mobile menu — the collection nav collapses below md (there's no room
              for it inline), so a hamburger opens a Sheet with the nav + search.
              Hidden from md up, where the inline nav is visible. */}
          {storeSlug && navItems.length > 0 && (
            <Sheet>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Open menu"
                  className="md:hidden inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-line bg-paper text-ink"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 6h18M3 12h18M3 18h18" />
                  </svg>
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[17rem] max-w-[85vw]">
                <SheetHeader>
                  <SheetTitle>{c.logoText || displayName}</SheetTitle>
                </SheetHeader>
                {c.showSearch &&
                  (mobileSearchSlot ?? (
                    <form action={`/s/${storeSlug}/search`} method="get" className="mt-4">
                      <Input
                        type="search"
                        name="q"
                        placeholder="Search products…"
                        className="h-9 w-full bg-bg text-sm"
                      />
                    </form>
                  ))}
                <nav className="mt-4 flex flex-col gap-0.5">
                  {navItems.map((n) => (
                    <SheetClose asChild key={`m-${n.name}-${n.href}`}>
                      <a
                        href={n.href}
                        {...(n.external ? { rel: "noopener noreferrer", target: "_blank" } : {})}
                        className="rounded-sm px-3 py-2.5 text-sm font-medium text-ink no-underline hover:bg-raised"
                      >
                        {n.name}
                      </a>
                    </SheetClose>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          )}

          {/* Identity */}
          <a
            href={storeSlug ? `/s/${storeSlug}` : "/"}
            style={{
              textDecoration: "none",
              color: "inherit",
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            title={storeName}
          >
            {/* Logo is additive, not either/or: show the image if set, and the
                wordmark beside it when text is explicitly typed — so a store can
                have an icon + wordmark, an image only, or text only.
                  · image + text → both (icon + wordmark)
                  · image, no text → just the image
                  · no image → the brand mark + text (or the store name) */}
            {logoUrl ? (
              <>
                <img
                  src={logoUrl}
                  alt={`${storeName} logo`}
                  // Height drives the size (theme control); width is auto so the
                  // logo keeps its aspect. The generous max-width only clips an
                  // extremely wide logo so it can't crowd out the nav.
                  style={{
                    height: `${logoHeight ?? 28}px`,
                    width: "auto",
                    maxWidth: "320px",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
                {c.logoText ? (
                  <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "0.9375rem" }}>
                    {c.logoText}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                {logoMark}
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "0.9375rem" }}>
                  {c.logoText || displayName}
                </span>
              </>
            )}
          </a>

          {/* Nav */}
          {storeSlug && navItems.length > 0 && (
            <nav
              className="sf-header-nav"
              style={{ display: "flex", gap: "0.25rem", flex: 1, justifyContent: "center", overflow: "hidden" }}
            >
              {navItems.map((n) => (
                <a
                  key={`${n.name}-${n.href}`}
                  href={n.href}
                  {...(n.external ? { rel: "noopener noreferrer", target: "_blank" } : {})}
                  style={{
                    padding: "0.25rem 0.625rem",
                    borderRadius: "var(--r-sm)",
                    fontSize: "0.8125rem",
                    color: "var(--muted)",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {n.name}
                </a>
              ))}
            </nav>
          )}

          {/* Nav grows to fill the middle so the right cluster hugs the edge;
              when there is no nav this spacer keeps the search + cart on the right. */}
          {!(storeSlug && navItems.length > 0) && <div style={{ flex: 1 }} />}

          {/* Right cluster — search sits on the right, immediately left of the cart. */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
            {c.showSearch &&
              storeSlug &&
              (searchSlot ?? (
                <form
                  className="sf-header-search"
                  action={`/s/${storeSlug}/search`}
                  method="get"
                  style={{ width: "clamp(8.5rem, 22vw, 15rem)", display: "flex" }}
                >
                  <Input
                    type="search"
                    name="q"
                    placeholder="Search products…"
                    className="h-8 rounded-sm bg-bg px-3 text-[0.8125rem] placeholder:text-[0.8125rem]"
                  />
                </form>
              ))}
            {c.showCart && storeSlug && (
              <a
                href={`/s/${storeSlug}/cart`}
                style={{ position: "relative", display: "inline-flex", alignItems: "center", padding: "0.375rem", color: "var(--ink)", textDecoration: "none" }}
                title="Cart"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                  <path d="M3 6h18" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                {cartBadge}
              </a>
            )}
            {c.showAccount ? accountSlot : null}
          </div>
        </div>
      </div>
    </header>
  );
}
